"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { Clock, MessageCircle } from "lucide-react";
import { PillOption } from "@/components/ui/pill-option";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CUSTOM_PREFIX, isCustomTime, parsePickupTime } from "@/lib/pickup-time";

export { isCustomTime, parsePickupTime };

const DEFAULT_START = "12:00";
const DEFAULT_END = "18:00";
const MINIMUM_CUSTOM_HOUR = 10;
const MAXIMUM_CUSTOM_HOUR = 20;
const MINUTE_OPTIONS = ["00", "15", "30", "45"] as const;

interface TimeSlotPickerProps {
  value: string;
  onChange: (value: string) => void;
  contactLabel: string;
  contactDescription: string;
  startTime?: string;
  endTime?: string;
  label?: string;
  /**
   * When true (default), auto-select the first available slot whenever no
   * time is chosen. Set to `false` to leave the time empty until the user
   * picks one — used by the order-flow date step where time is treated as
   * an explicit user choice that gates auto-advance to the next step.
   */
  autoSelectFirstSlot?: boolean;
}

function parseTime(time: string): number {
  const parts = time.split(":").map(Number);
  if (parts.length !== 2 || isNaN(parts[0]) || isNaN(parts[1])) return -1;
  return parts[0] * 60 + parts[1];
}

function generateSlots(start: string, end: string): string[] {
  const startMinutes = parseTime(start);
  const endMinutes = parseTime(end);

  // Guard against bad input — fall back to defaults
  if (startMinutes < 0 || endMinutes < 0 || endMinutes < startMinutes) {
    return generateSlots(DEFAULT_START, DEFAULT_END);
  }

  const slots: string[] = [];
  for (let m = startMinutes; m <= endMinutes; m += 30) {
    const h = Math.floor(m / 60);
    const min = m % 60;
    slots.push(`${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`);
  }

  return slots;
}

export function TimeSlotPicker({
  value,
  onChange,
  contactLabel,
  contactDescription,
  startTime = DEFAULT_START,
  endTime = DEFAULT_END,
  label,
  autoSelectFirstSlot = true,
}: TimeSlotPickerProps) {
  const isCustomSelected = isCustomTime(value);
  const [showCustomInput, setShowCustomInput] = useState(isCustomSelected);

  // Sync custom input visibility when value resets externally (e.g. date change)
  useEffect(() => {
    if (!value) {
      setShowCustomInput(false);
    }
  }, [value]);

  const slots = useMemo(
    () => generateSlots(startTime, endTime),
    [startTime, endTime],
  );

  // Preselect the first available slot whenever no time is chosen
  // (initial mount, after date change, or after closing custom picker).
  // Skipped when `autoSelectFirstSlot` is false — used by the order-flow
  // date step where time selection gates auto-advance.
  useEffect(() => {
    if (!autoSelectFirstSlot) return;
    if (!value && !showCustomInput && slots.length > 0) {
      onChange(slots[0]);
    }
  }, [autoSelectFirstSlot, slots, value, showCustomInput, onChange]);

  const handleSlotClick = useCallback(
    (slot: string) => {
      setShowCustomInput(false);
      onChange(slot);
    },
    [onChange],
  );

  const handleCustomToggle = useCallback(() => {
    if (showCustomInput && isCustomSelected) {
      // Closing custom picker — clear value
      setShowCustomInput(false);
      onChange("");
    } else {
      setShowCustomInput(true);
      // Don't change the value yet; let them pick a time
    }
  }, [showCustomInput, isCustomSelected, onChange]);

  const customHour = isCustomSelected
    ? parsePickupTime(value).split(":")[0] ?? ""
    : "";
  const customMinute = isCustomSelected
    ? parsePickupTime(value).split(":")[1] ?? ""
    : "";

  const handleCustomHourChange = useCallback(
    (hour: string) => {
      const minute = customMinute || "00";
      onChange(`${CUSTOM_PREFIX}${hour}:${minute}`);
    },
    [customMinute, onChange],
  );

  const handleCustomMinuteChange = useCallback(
    (minute: string) => {
      const hour = customHour || String(MINIMUM_CUSTOM_HOUR).padStart(2, "0");
      onChange(`${CUSTOM_PREFIX}${hour}:${minute}`);
    },
    [customHour, onChange],
  );

  const hourOptions = useMemo(() => {
    const hours: string[] = [];
    for (let h = MINIMUM_CUSTOM_HOUR; h <= MAXIMUM_CUSTOM_HOUR; h++) {
      hours.push(String(h).padStart(2, "0"));
    }
    return hours;
  }, []);

  const selectedDisplay = useMemo(() => {
    if (!value) return null;
    if (isCustomSelected) {
      return parsePickupTime(value);
    }
    return value;
  }, [value, isCustomSelected]);

  return (
    <div className="space-y-2">
      {label && (
        <div className="flex items-center gap-1.5">
          <Clock className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
          <span className="text-sm font-medium text-foreground">{label}</span>
        </div>
      )}

      {/* Time slots + custom time chip */}
      <div className="flex flex-wrap gap-1.5">
        {slots.map((slot) => (
          <PillOption
            key={slot}
            selected={value === slot}
            onClick={() => handleSlotClick(slot)}
          >
            {slot}
          </PillOption>
        ))}
        <PillOption
          selected={showCustomInput}
          onClick={handleCustomToggle}
          className="gap-1.5"
        >
          <MessageCircle className="h-3 w-3 shrink-0" aria-hidden="true" />
          {contactLabel}
        </PillOption>
      </div>

      {/* Custom time selectors */}
      {showCustomInput && (
        <div className="space-y-1.5">
          <div className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-2 py-1">
            <Clock className="h-3 w-3 shrink-0 text-foreground" aria-hidden="true" />
            <Select value={customHour} onValueChange={handleCustomHourChange}>
              <SelectTrigger
                className="h-7 w-[52px] rounded-md border-border px-2 text-xs"
                aria-label="Hour"
              >
                <SelectValue placeholder="HH" />
              </SelectTrigger>
              <SelectContent>
                {hourOptions.map((h) => (
                  <SelectItem key={h} value={h}>
                    {h}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs font-medium text-foreground">:</span>
            <Select value={customMinute} onValueChange={handleCustomMinuteChange}>
              <SelectTrigger
                className="h-7 w-[52px] rounded-md border-border px-2 text-xs"
                aria-label="Minute"
              >
                <SelectValue placeholder="MM" />
              </SelectTrigger>
              <SelectContent>
                {MINUTE_OPTIONS.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground">{contactDescription}</p>
        </div>
      )}

      {/* Selected indicator */}
      {selectedDisplay && (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" aria-hidden="true" />
          {selectedDisplay}
        </p>
      )}
    </div>
  );
}
